import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Search, User, Building2, Briefcase, Send, Copy, CheckCircle2 } from 'lucide-react';
import { bullhornApi, formsApi, getErrorMessage } from '../../services/api';

const ENTITY_ICONS = {
  candidate: User,
  contact: Building2,
  placement: Briefcase,
  company: Building2,
};

/**
 * Recipient picker + form-send dialog.
 *
 * Mirrors the Bullhorn-record lookup pattern used by SendDocumentPage but is
 * intentionally simpler — forms have no PDF positioning, so the dialog only
 * needs to resolve a Bullhorn entity → email and POST `/forms/send`.
 */
export const SendFormDialog = ({ form, open, onClose }) => {
  const entityType = form?.entity_type || 'candidate';
  const Icon = ENTITY_ICONS[entityType] || User;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [expiryHours, setExpiryHours] = useState(168);
  const [sending, setSending] = useState(false);
  const [sentLink, setSentLink] = useState(null);

  // Debounced search against the appropriate Bullhorn endpoint.
  useEffect(() => {
    if (selected || !query || query.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        let res;
        if (entityType === 'candidate') res = await bullhornApi.searchCandidates(query);
        else if (entityType === 'contact') res = await bullhornApi.searchContacts(query);
        else if (entityType === 'placement') res = await bullhornApi.searchPlacements(query);
        else res = await bullhornApi.searchCompanies(query);
        // Bullhorn endpoints return `{<entity>s: [...], mock}` — pluck the
        // right key for the active entity type. Fall through to `results`
        // / `data` for any future endpoints that follow a different shape.
        const data = res.data || {};
        const list =
          data.candidates ||
          data.contacts ||
          data.placements ||
          data.companies ||
          data.results ||
          (Array.isArray(data) ? data : []);
        setResults(Array.isArray(list) ? list : []);
      } catch (err) {
        // Bullhorn might not be connected — degrade gracefully so the
        // dialog still lets the recruiter type a manual email.
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, entityType, selected]);

  const handlePick = useCallback((r) => {
    // Normalise the shape across the 4 search endpoints.
    const name = r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.candidate?.name || `#${r.id}`;
    const email = r.email || r.candidate?.email || r.clientContact?.email || '';
    setSelected({
      bullhorn_id: String(r.id),
      name,
      email,
    });
    setQuery(name);
    setResults([]);
  }, []);

  const handleSend = async () => {
    if (!selected) {
      toast.error('Please pick a recipient');
      return;
    }
    if (!selected.email) {
      toast.error('No email on this record');
      return;
    }
    setSending(true);
    try {
      const { data } = await formsApi.send({
        form_id: form.id,
        bullhorn_id: selected.bullhorn_id,
        entity_type: entityType,
        candidate_name: selected.name,
        candidate_email: selected.email,
        expiry_hours: expiryHours,
        message: message || undefined,
      });
      setSentLink({
        url: data.completion_url,
        emailStatus: data.email_status,
        expiresAt: data.expires_at,
      });
      if (data.email_status === 'sent') {
        toast.success(`Form sent to ${selected.email}`);
      } else if (data.email_status === 'failed') {
        toast.warning('Saved, but email delivery failed. Copy the link below.');
      } else {
        toast.success('Form link ready — email may be queued.');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send form'));
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setSelected(null);
    setMessage('');
    setSentLink(null);
    setExpiryHours(168);
    onClose();
  };

  const copyLink = () => {
    if (!sentLink?.url) return;
    navigator.clipboard.writeText(sentLink.url);
    toast.success('Link copied');
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl" data-testid="send-form-dialog">
        <DialogHeader>
          <DialogTitle>Send "{form.name}"</DialogTitle>
          <DialogDescription>
            Pick a Bullhorn {entityType} and we'll email them a secure link to complete the form.
          </DialogDescription>
        </DialogHeader>

        {sentLink ? (
          <div className="space-y-4 py-2" data-testid="send-form-success">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-emerald-900">Form sent to {selected.email}</p>
                <p className="text-xs text-emerald-700 mt-1">
                  {sentLink.emailStatus === 'failed'
                    ? 'Email delivery failed — share the link manually.'
                    : 'The candidate has been notified.'}
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Direct link</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  readOnly
                  value={sentLink.url}
                  data-testid="send-form-link-input"
                  className="font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <Button variant="outline" onClick={copyLink} className="gap-1.5" data-testid="send-form-copy-btn">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose} data-testid="send-form-done-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-2">
              <div>
                <Label className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" /> Recipient ({entityType})
                </Label>
                <div className="relative mt-1.5">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="send-form-search-input"
                    placeholder={`Search ${entityType} by name or email`}
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setSelected(null);
                    }}
                    className="pl-9"
                  />
                </div>
                {results.length > 0 && !selected && (
                  <div className="border rounded-lg mt-1 bg-white shadow-sm max-h-56 overflow-auto" data-testid="send-form-results">
                    {results.slice(0, 8).map((r) => {
                      const name = r.name || `${r.firstName || ''} ${r.lastName || ''}`.trim() || `#${r.id}`;
                      const email = r.email || r.candidate?.email || r.clientContact?.email || '';
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => handlePick(r)}
                          className="block w-full text-left px-3 py-2 hover:bg-primary/5 border-b last:border-b-0"
                          data-testid={`send-form-result-${r.id}`}
                        >
                          <div className="text-sm font-medium">{name}</div>
                          <div className="text-xs text-muted-foreground">{email || 'No email on record'}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {searching && <p className="text-xs text-muted-foreground mt-1">Searching…</p>}
              </div>

              {selected && (
                <>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="send-form-selected">
                    <p className="text-sm font-medium text-emerald-900">{selected.name}</p>
                    <p className="text-xs text-emerald-700">{selected.email || 'No email on record'}</p>
                  </div>

                  {!selected.email && (
                    <div>
                      <Label>Recipient email (override)</Label>
                      <Input
                        type="email"
                        value={selected.email || ''}
                        onChange={(e) => setSelected({ ...selected, email: e.target.value })}
                        data-testid="send-form-email-input"
                        placeholder="candidate@example.com"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Optional message</Label>
                    <Textarea
                      rows={2}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="A personal note for the recipient (optional)"
                      data-testid="send-form-message-input"
                    />
                  </div>

                  <div>
                    <Label>Link expires in</Label>
                    <Select value={String(expiryHours)} onValueChange={(v) => setExpiryHours(Number(v))}>
                      <SelectTrigger data-testid="send-form-expiry-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24">1 day</SelectItem>
                        <SelectItem value="72">3 days</SelectItem>
                        <SelectItem value="168">7 days</SelectItem>
                        <SelectItem value="336">14 days</SelectItem>
                        <SelectItem value="720">30 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} data-testid="send-form-cancel-btn">Cancel</Button>
              <Button
                onClick={handleSend}
                disabled={!selected || !selected.email || sending}
                className="gap-1.5"
                data-testid="send-form-submit-btn"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Sending…' : 'Send Form'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
